import axios, { type AxiosInstance } from 'axios'

import { AuthenticationV2Sdk } from './sdks/authenticationV2'
import type { UserSdk } from './sdks/user'
import type { Configuration } from './generated'
import { addAxiosRequestInterceptors } from './interceptors'
import { TrackingSdk } from './tracking'

export type InitCoreSdkParams = {
  configuration: Configuration
  userSdk: UserSdk
  shouldIgnoreAuthInterceptor?: boolean
}
export type CoreSdkReturn<Params extends InitCoreSdkParams> = Params extends { shouldIgnoreAuthInterceptor: true }
  ? {
      coreSdk: {
        trackingSdk: TrackingSdk
      }
      axiosInstance: AxiosInstance
    }
  : {
      coreSdk: {
        trackingSdk: TrackingSdk
        authenticationV2Sdk: AuthenticationV2Sdk
      }
      axiosInstance: AxiosInstance
    }

export const initCoreSdk = <Params extends InitCoreSdkParams>({
  configuration,
  userSdk,
  shouldIgnoreAuthInterceptor,
}: Params): CoreSdkReturn<Params> => {
  const axiosInstance = axios.create()

  const trackingSdk = new TrackingSdk(configuration, userSdk, axiosInstance)

  const authenticationV2Sdk = shouldIgnoreAuthInterceptor
    ? undefined
    : new AuthenticationV2Sdk(trackingSdk, configuration, userSdk, axiosInstance)

  addAxiosRequestInterceptors({
    axios: axiosInstance,
    configuration,
    userSdk,
    trackingSdk,
    authenticationV2Sdk,
  })

  if (shouldIgnoreAuthInterceptor) {
    return { coreSdk: { trackingSdk }, axiosInstance } as CoreSdkReturn<Params>
  }

  return { coreSdk: { trackingSdk, authenticationV2Sdk }, axiosInstance } as CoreSdkReturn<Params>
}
